import { GoogleMap } from '@react-google-maps/api'
import { observer } from 'mobx-react'
import React, { PropsWithChildren, useRef, useState } from 'react'

import { isMobile } from '@flowwai-frontend/shared/utils/web'

import { MAPS_NO_DATA_LOCATION, MAPS_ZOOM_VALUES } from '../../constants'
import { imageUrls } from '../../constants/imageConstants'
import { useMapsUIStore } from '../../hooks/useMapsUIStore'
import { useMapsUtilityContext } from '../../hooks/useMapsUtilityContext'
import { MapStyleEnum } from '../../types'
import {
   MapsServiceProvidersConfig,
   MapViewStateType
} from '../../types/MapsServiceProviderTypes'
import { createOlaMapsCustomAttributionForGoogleMaps } from '../../utils'
import GoogleMapsContextProvider from '../GoogleMapsContextProvider/GoogleMapsContextProvider'
import MapBoxMapServiceProvider from '../MapBoxMapServiceProvider/MapBoxMapServiceProvider'

interface Props extends PropsWithChildren {
   config: MapsServiceProvidersConfig
}

const MapsServiceProvider = (props: Props): React.ReactElement => {
   const { config } = props

   const { isMapsLoaded } = useMapsUIStore()
   const { restrictToMapStyle } = useMapsUtilityContext()

   const [viewState, setViewState] = useState<MapViewStateType>({
      latitude: MAPS_NO_DATA_LOCATION.lat,
      longitude: MAPS_NO_DATA_LOCATION.lng,
      zoom: MAPS_ZOOM_VALUES.noDataZoom
   })
   const [mapStyleType, setMapStyleType] = useState<MapStyleEnum>(
      restrictToMapStyle ?? MapStyleEnum.Default
   )
   const [isEditMode, setIsEditMode] = useState(false)

   const googleMapRef = useRef<google.maps.Map | null>(null)
   const streetViewLayerRef = useRef<google.maps.StreetViewCoverageLayer | null>(
      null
   )

   const isMobileDevice = isMobile()

   const styleProps = {
      width: '100%',
      height: isMobileDevice ? 'calc(100% - 45px)' : '100%'
   }

   if (!isMapsLoaded) return <></>

   const markLocationCursor =
      mapStyleType === MapStyleEnum.Street
         ? 'pointer'
         : `url(${
              mapStyleType === MapStyleEnum.Satellite
                 ? imageUrls.markerCursorSatellite
                 : imageUrls.markerCursor
           }) 14 30, pointer`

   const isSearchProviderOlaMaps = config.search?.serviceProvider === 'OLA_MAPS'

   switch (config.maps.serviceProvider) {
      case 'GOOGLE_MAPS': {
         const selectedMapStyle =
            mapStyleType === MapStyleEnum.Default ? 'roadmap' : 'hybrid'

         const onZoomChanged = () => {
            const currentZoomLevel = googleMapRef.current?.getZoom()

            if (currentZoomLevel)
               setViewState({ ...viewState, zoom: currentZoomLevel })
         }

         const handleOnChangeMapStyle = (mapStyle: MapStyleEnum): void => {
            const isStreetViewSelected = mapStyle === MapStyleEnum.Street

            if (googleMapRef.current && streetViewLayerRef.current) {
               if (!isStreetViewSelected) {
                  streetViewLayerRef.current.setMap(null)
               } else {
                  streetViewLayerRef.current.setMap(googleMapRef.current)
               }
            }
            setMapStyleType(mapStyle)
         }

         const onMapLoad = (map: google.maps.Map): void => {
            googleMapRef.current = map
            streetViewLayerRef.current = new google.maps.StreetViewCoverageLayer()

            switch (config.search?.serviceProvider) {
               case 'OLA_MAPS': {
                  const olaAttribution = createOlaMapsCustomAttributionForGoogleMaps()
                  map?.controls[google.maps.ControlPosition.BOTTOM_RIGHT].push(
                     olaAttribution
                  )
                  break
               }
            }
         }

         return (
            <GoogleMap
               center={MAPS_NO_DATA_LOCATION}
               zoom={viewState.zoom}
               onZoomChanged={onZoomChanged}
               mapContainerStyle={styleProps}
               onLoad={onMapLoad}
               mapTypeId={selectedMapStyle}
               options={{
                  disableDefaultUI: true,
                  draggableCursor: isEditMode ? markLocationCursor : 'grab',
                  draggingCursor: 'grabbing'
               }}
               mapContainerClassName='maps-container'
            >
               <GoogleMapsContextProvider
                  isEditMode={isEditMode}
                  mapStyle={mapStyleType}
                  onChangeMapStyle={handleOnChangeMapStyle}
                  zoomValue={viewState.zoom}
                  onChangeZoomValue={zoom =>
                     setViewState({ ...viewState, zoom })
                  }
                  setIsEditMode={setIsEditMode}
               >
                  {props.children}
               </GoogleMapsContextProvider>
            </GoogleMap>
         )
      }
      case 'MAPBOX': {
         return (
            <MapBoxMapServiceProvider
               viewState={viewState}
               styleProps={styleProps}
               apiKey={config.maps.apiKey}
               setMapStyleType={setMapStyleType}
               setViewState={setViewState}
               mapStyleType={mapStyleType}
               isEditMode={isEditMode}
               setIsEditMode={setIsEditMode}
               isSearchProviderOlaMaps={isSearchProviderOlaMaps}
               markLocationCursor={markLocationCursor}
            >
               {props.children}
            </MapBoxMapServiceProvider>
         )
      }
   }
}

export default observer(MapsServiceProvider)
